import {
    Divider,
    BreadcrumbLink,
    VStack,
    Text,
    Grid,
    GridItem,
    HStack,
    IconButton,
    Icon,
    chakra,
    Tag,
    Box,
    Stack,
    useToken,
    StackDivider,
    Button,
    Tooltip,
    Progress,
    Link as ChakraLink,
} from '@chakra-ui/react';
import { ChatDetailViewProps } from './chatDetailProps';
import { ChatMessageProps, MessagePartProps } from './message';
import React from 'react';
import { ReportingShellViewBaseConfig } from '../../../base';
import { BiBrain } from 'react-icons/bi';
import { ChatApplyContainerProps, ChatApplyViewProps } from './apply';
import { HiOutlineArrowTurnDownLeft, HiOutlineBackspace } from 'react-icons/hi2';
import { ChatItemCreateContainerProps, ChatItemCreateViewProps } from './create';
import { ChatComposerContainerProps, ChatComposerViewProps } from '../common';
import { ChatItemSearchContainerProps, ChatItemSearchViewProps } from './search';
import { ChatItemReadContainerProps, ChatItemReadViewProps } from './read';
import { ChatItemTaskContainerProps, ChatItemTaskViewProps } from './task';
import { ChatItemReplaceContainerProps, ChatItemReplaceViewProps } from './replace';

export function createChatDetailView(
    config: ReportingShellViewBaseConfig & {
        Container: {
            Item: {
                Apply: React.FC<ChatApplyContainerProps>;
                Create: React.FC<ChatItemCreateContainerProps>;
                Search: React.FC<ChatItemSearchContainerProps>;
                Read: React.FC<ChatItemReadContainerProps>;
                Task: React.FC<ChatItemTaskContainerProps>;
                Replace: React.FC<ChatItemReplaceContainerProps>;
            };
            Composer: React.FC<ChatComposerContainerProps>;
        };
    }
): React.FC<ChatDetailViewProps> {
    const {
        UI: {
            Application: { Anchor, Link },
        },
        Container: {
            Item: {
                Apply: ApplyItemContainer,
                Create: CreateItemContainer,
                Search: SearchItemContainer,
                Read: ReadItemContainer,
                Task: TaskItemContainer,
                Replace: ReplaceItemContainer,
            },
            Composer: ComposerContainer,
        },
    } = config;

    const ReplaceItem: React.FC<ChatItemReplaceViewProps> = (props) => {
        return (
            <VStack w="full" align="start">
                <HStack fontSize="sm" fontWeight="medium" spacing={2}>
                    <Icon fontSize="md" as={props.Icon} color="whiteAlpha.400" />
                    <Text color="whiteAlpha.500">{props.label}</Text>
                </HStack>
                {!props.isLoading && (
                    <VStack
                        w="full"
                        borderWidth={1}
                        borderColor="whiteAlpha.200"
                        borderRadius="md"
                        color="whiteAlpha.800"
                        spacing={0}
                        divider={<StackDivider color="whiteAlpha.200" />}
                        fontSize="sm"
                    >
                        <HStack
                            w="full"
                            justify="space-between"
                            px={{ base: 2, xl: 3 }}
                            py={{ base: 0.5, xl: 1 }}
                        >
                            <HStack
                                onClick={props.document.onClick}
                                w="full"
                                fontWeight="medium"
                                cursor="pointer"
                                role="group"
                            >
                                {props.document.icon.kind === 'emoji' && (
                                    <Text noOfLines={1}>{props.document.icon.text}</Text>
                                )}
                                {props.document.icon.kind === 'icon' && (
                                    <Icon as={props.document.icon.Component} />
                                )}
                                <Text
                                    noOfLines={1}
                                    userSelect="none"
                                    _groupHover={{ textDecoration: 'underline' }}
                                >
                                    {props.document.label}
                                </Text>
                            </HStack>
                            {props.summary && (
                                <HStack userSelect="none" spacing={2}>
                                    <HStack spacing={1}>
                                        {Array.from({
                                            length: props.summary.visual.squares.length,
                                        }).map((_, index) => (
                                            <Box
                                                key={index}
                                                w={2.5}
                                                h={2.5}
                                                bg="green.300"
                                                borderRadius="sm"
                                            />
                                        ))}
                                    </HStack>
                                    <HStack spacing={1}>
                                        <Text
                                            color="green.300"
                                            fontSize="xs"
                                            fontWeight="semibold"
                                        >
                                            {props.summary.added.label}
                                        </Text>
                                    </HStack>
                                </HStack>
                            )}
                        </HStack>
                    </VStack>
                )}
            </VStack>
        );
    };

    const TaskItem: React.FC<ChatItemTaskViewProps> = (props) => {
        return (
            <VStack w="full" align="start">
                <HStack fontSize="sm" fontWeight="medium" spacing={2}>
                    <Icon fontSize="md" as={props.Icon} color="whiteAlpha.400" />
                    <Text color="whiteAlpha.500">{props.label}</Text>
                </HStack>
                {props.task && (
                    <VStack
                        w="full"
                        borderWidth={1}
                        borderColor="whiteAlpha.200"
                        borderRadius="md"
                        color="whiteAlpha.800"
                        spacing={0}
                        divider={<StackDivider color="whiteAlpha.200" />}
                        fontSize="sm"
                    >
                        <HStack
                            w="full"
                            px={{ base: 2, xl: 3 }}
                            py={{ base: 0.5, xl: 1 }}
                            fontWeight="medium"
                            role="group"
                        >
                            <VStack align="start" spacing={0} w="full">
                                <Tooltip
                                    placement="top"
                                    px={2}
                                    py={1}
                                    bg="gray.50"
                                    color="gray.900"
                                    shouldWrapChildren={true}
                                    // label={props.description}
                                    // isDisabled={!props.description}
                                >
                                    <HStack
                                        fontWeight="medium"
                                        color="whiteAlpha.800"
                                        userSelect="none"
                                        maxW="space-between"
                                        spacing={6}
                                        w="full"
                                    >
                                        <HStack w="full">
                                            <ChakraLink
                                                as={Link}
                                                {...props.task.link}
                                                noOfLines={1}
                                            >
                                                {props.task.label}
                                            </ChakraLink>
                                        </HStack>
                                        <HStack flexShrink={0}>
                                            <ChakraLink
                                                as={Link}
                                                {...props.task.link}
                                                fontSize="xs"
                                                color="whiteAlpha.500"
                                                noOfLines={1}
                                            >
                                                View all
                                            </ChakraLink>
                                        </HStack>
                                    </HStack>
                                </Tooltip>
                                {/* <HStack>
                                    {props.icon?.kind === 'emoji' && (
                                        <Text>{props.icon.text}</Text>
                                    )}
                                    <Text color="whiteAlpha.500">{props.note}</Text>
                                </HStack> */}
                            </VStack>
                        </HStack>
                        <VStack
                            w="full"
                            spacing={2}
                            px={{ base: 2, xl: 3 }}
                            py={{ base: 2, xl: 3 }}
                        >
                            <HStack w="full" justify="space-between">
                                <Text color="whiteAlpha.600">
                                    {props.task.step.label}
                                </Text>
                                <Tooltip
                                    placement="top"
                                    px={2}
                                    py={1}
                                    bg="gray.50"
                                    color="gray.900"
                                    shouldWrapChildren={true}
                                    isDisabled={!props.task.status.tooltip}
                                    label={props.task.status.tooltip?.label}
                                >
                                    <Tag
                                        userSelect="none"
                                        colorScheme={
                                            props.task.status.colorScheme ?? undefined
                                        }
                                        size="sm"
                                    >
                                        {props.task.status.label}
                                    </Tag>
                                </Tooltip>
                            </HStack>
                            {props.task.progress && (
                                <Progress
                                    w="full"
                                    bg="whiteAlpha.200"
                                    {...props.task.progress}
                                />
                            )}
                        </VStack>
                    </VStack>
                )}
            </VStack>
        );
    };

    const SearchItem: React.FC<ChatItemSearchViewProps> = (props) => {
        return (
            <HStack fontSize="sm" fontWeight="medium" spacing={2}>
                <Icon fontSize="md" as={props.Icon} color="whiteAlpha.400" />
                <Text color="whiteAlpha.500" noOfLines={1} maxW="12rem">
                    {props.label}
                </Text>
            </HStack>
        );
    };

    const ReadItem: React.FC<ChatItemReadViewProps> = (props) => {
        return (
            <HStack fontSize="sm" fontWeight="medium" spacing={2}>
                <Icon fontSize="md" as={props.Icon} color="whiteAlpha.400" />
                <Text color="whiteAlpha.500">{props.label}</Text>
            </HStack>
        );
    };

    const CreateItem: React.FC<ChatItemCreateViewProps> = (props) => {
        return (
            <VStack w="full" align="start">
                <HStack fontSize="sm" fontWeight="medium" spacing={2}>
                    <Icon fontSize="md" as={props.Icon} color="whiteAlpha.400" />
                    <Text color="whiteAlpha.500">{props.label}</Text>
                </HStack>
                <VStack
                    w="full"
                    borderWidth={1}
                    borderColor="whiteAlpha.200"
                    borderRadius="md"
                    color="whiteAlpha.800"
                    spacing={0}
                    divider={<StackDivider color="whiteAlpha.200" />}
                    fontSize="sm"
                >
                    <HStack
                        w="full"
                        justify="space-between"
                        px={{ base: 2, xl: 3 }}
                        py={{ base: 0.5, xl: 1 }}
                    >
                        <HStack
                            onClick={props.document.onClick}
                            w="full"
                            fontWeight="medium"
                            cursor="pointer"
                            role="group"
                        >
                            {props.document.icon.kind === 'emoji' && (
                                <Text noOfLines={1}>{props.document.icon.text}</Text>
                            )}
                            {props.document.icon.kind === 'icon' && (
                                <Icon as={props.document.icon.Component} />
                            )}
                            <Text
                                noOfLines={1}
                                userSelect="none"
                                _groupHover={{ textDecoration: 'underline' }}
                            >
                                {props.document.label}
                            </Text>
                        </HStack>
                        <HStack userSelect="none" spacing={2}>
                            <HStack spacing={1}>
                                {Array.from({
                                    length: props.blocks.visualization.size,
                                }).map((_, index) => (
                                    <Box
                                        key={index}
                                        w={2.5}
                                        h={2.5}
                                        bg="green.300"
                                        borderRadius="sm"
                                    />
                                ))}
                            </HStack>
                            <HStack spacing={1}>
                                <Text
                                    color="green.300"
                                    fontSize="xs"
                                    fontWeight="semibold"
                                >
                                    {props.blocks.added.label}
                                </Text>
                            </HStack>
                        </HStack>
                    </HStack>
                </VStack>
            </VStack>
        );
    };

    const ApplyItem: React.FC<ChatApplyViewProps> = (props) => {
        return (
            <VStack
                w="full"
                borderWidth={1}
                borderColor="whiteAlpha.200"
                borderRadius="md"
                color="whiteAlpha.800"
                spacing={0}
                divider={<StackDivider color="whiteAlpha.200" />}
                fontSize="sm"
            >
                <HStack
                    onClick={props.document.onClick}
                    w="full"
                    px={{ base: 2, xl: 3 }}
                    py={{ base: 0.5, xl: 1 }}
                    fontWeight="medium"
                    cursor="pointer"
                    role="group"
                >
                    {props.document.icon.kind === 'emoji' && (
                        <Text noOfLines={1}>{props.document.icon.text}</Text>
                    )}
                    {props.document.icon.kind === 'icon' && (
                        <Icon as={props.document.icon.Component} />
                    )}
                    <Text
                        noOfLines={1}
                        userSelect="none"
                        _groupHover={{ textDecoration: 'underline' }}
                    >
                        {props.document.label}
                    </Text>
                </HStack>
                <VStack
                    align="start"
                    w="full"
                    spacing={1}
                    px={{ base: 2, xl: 3 }}
                    py={{ base: 1, xl: 1 }}
                    divider={<StackDivider borderColor="whiteAlpha.200" />}
                    // fontSize="xs"
                >
                    {props.operations.map((operation) => {
                        return (
                            <HStack
                                key={operation.id}
                                w="full"
                                justify="space-between"
                                fontWeight="medium"
                            >
                                <HStack userSelect="none" spacing={2}>
                                    <HStack spacing={1}>
                                        {operation.summary.visual.squares.map(
                                            (square) => (
                                                <Box
                                                    key={square.id}
                                                    w={3}
                                                    h={3}
                                                    borderRadius="sm"
                                                    bg={
                                                        square.type === 'added'
                                                            ? 'green.300'
                                                            : 'red.300'
                                                    }
                                                />
                                            )
                                        )}
                                    </HStack>
                                    <HStack spacing={1}>
                                        <Text color="red.300">
                                            {operation.summary.removed.label}
                                        </Text>
                                        <Text color="green.300">
                                            {operation.summary.added.label}
                                        </Text>
                                    </HStack>
                                </HStack>
                                {operation.status.kind === 'skipped' && <></>}
                                {operation.status.kind === 'accepted' && (
                                    <Text fontWeight="medium" color="green.300">
                                        {operation.status.label}
                                    </Text>
                                )}
                                {operation.status.kind === 'rejected' && (
                                    <Text fontWeight="medium" color="red.300">
                                        {operation.status.label}
                                    </Text>
                                )}
                                {operation.status.kind === 'pending' && (
                                    <HStack spacing={2}>
                                        <Button
                                            {...operation.status.reject.button}
                                            borderRadius="sm"
                                            size="xs"
                                            h={5}
                                            px={2}
                                            bg="none"
                                            iconSpacing={1}
                                            color="whiteAlpha.600"
                                            rightIcon={
                                                <Icon
                                                    fontSize="md"
                                                    as={HiOutlineBackspace}
                                                />
                                            }
                                            _hover={{ bg: 'whiteAlpha.100' }}
                                        >
                                            {operation.status.reject.label}
                                        </Button>
                                        <Button
                                            {...operation.status.accept.button}
                                            borderRadius="sm"
                                            size="xs"
                                            h={5}
                                            px={2}
                                            iconSpacing={1}
                                            bg="blue.400"
                                            color="whiteAlpha.800"
                                            rightIcon={
                                                <Icon as={HiOutlineArrowTurnDownLeft} />
                                            }
                                            _hover={{ bg: 'blue.500' }}
                                        >
                                            {operation.status.accept.label}
                                        </Button>
                                    </HStack>
                                )}
                            </HStack>
                        );
                    })}
                </VStack>
            </VStack>
        );
    };

    const Message: React.FC<ChatMessageProps> = (message) => {
        const [whiteAlpha500, whiteAlpha300, whiteAlpha400] = useToken('colors', [
            'whiteAlpha.500',
            'whiteAlpha.300',
            'whiteAlpha.400',
        ]);

        if (message.role === 'user') {
            return (
                <HStack w="full" justify="end">
                    <VStack
                        px={{ base: 3, '2xl': 4 }}
                        py={{ base: 2, '2xl': 3 }}
                        bg="whiteAlpha.100"
                        borderRadius="md"
                        maxW={{ base: 'full', lg: '85%', '2xl': '75%' }}
                        align="start"
                        color="whiteAlpha.800"
                    >
                        {message.context && (
                            <HStack spacing={{ base: 3, '2xl': 4 }} align="start">
                                <Box
                                    w={1}
                                    my={1}
                                    alignSelf="stretch"
                                    bg="whiteAlpha.200"
                                    borderRadius="md"
                                />
                                <VStack
                                    w="full"
                                    align="start"
                                    fontSize="xs"
                                    spacing={{ base: 1, '2xl': 1 }}
                                >
                                    <HStack spacing={1}>
                                        <Text
                                            color="whiteAlpha.700"
                                            fontWeight="semibold"
                                            noOfLines={1}
                                            wordBreak="break-all"
                                        >
                                            {message.context.title}
                                        </Text>
                                        {message.context.range.label && (
                                            <Text
                                                color="whiteAlpha.400"
                                                fontSize="xs"
                                                fontWeight="medium"
                                            >
                                                {message.context.range.label}
                                            </Text>
                                        )}
                                    </HStack>
                                    <Text color="whiteAlpha.500" noOfLines={2}>
                                        {message.context.text}
                                    </Text>
                                </VStack>
                            </HStack>
                        )}
                        {message.parts.map((part: MessagePartProps, index: number) => {
                            if (part.type === 'text') {
                                return (
                                    <Text
                                        key={part.text}
                                        color="whiteAlpha.800"
                                        wordBreak="break-all"
                                        sx={{ hyphens: 'auto' }}
                                    >
                                        {part.text}
                                    </Text>
                                );
                            }
                            console.warn(`not supported: ${part.type}`);
                            return <>not supported</>;
                        })}
                    </VStack>
                </HStack>
            );
        }
        return (
            <HStack w="full" justify="start" color="whiteAlpha.700">
                <VStack py={{ base: 2, '2xl': 3 }} w="full" align="start">
                    {message.parts.map((part: MessagePartProps, index: number) => {
                        if (part.type === 'text') {
                            return <Text key={part.text}>{part.text}</Text>;
                        }
                        if (part.item && part.item.kind === 'apply') {
                            return (
                                <ApplyItemContainer
                                    key={index}
                                    as={ApplyItem}
                                    item={part.item.data.item}
                                />
                            );
                        }
                        if (part.item && part.item.kind === 'create') {
                            return (
                                <CreateItemContainer
                                    key={index}
                                    as={CreateItem}
                                    item={part.item.data.item}
                                />
                            );
                        }
                        if (part.item && part.item.kind === 'search') {
                            return (
                                <SearchItemContainer
                                    key={index}
                                    as={SearchItem}
                                    item={part.item.data.item}
                                />
                            );
                        }
                        if (part.item && part.item.kind === 'replace') {
                            return (
                                <ReplaceItemContainer
                                    key={index}
                                    as={ReplaceItem}
                                    item={part.item.data.item}
                                />
                            );
                        }
                        if (part.item && part.item.kind === 'task') {
                            return (
                                <TaskItemContainer
                                    key={index}
                                    as={TaskItem}
                                    item={part.item.data.item}
                                />
                            );
                        }
                        if (part.item && part.item.kind === 'read') {
                            return (
                                <ReadItemContainer
                                    key={index}
                                    as={ReadItem}
                                    item={part.item.data.item}
                                />
                            );
                        }
                        if (part.type === 'tool-invocation') {
                            const isRunning = part.status === 'running';
                            return (
                                <HStack
                                    key={index}
                                    fontSize="sm"
                                    fontWeight="medium"
                                    spacing={2}
                                    position="relative"
                                >
                                    {part.Icon ? (
                                        <Icon
                                            fontSize="md"
                                            as={part.Icon}
                                            color="whiteAlpha.400"
                                            sx={
                                                isRunning
                                                    ? {
                                                          animation:
                                                              'iconShine 4s linear infinite',
                                                          '@keyframes iconShine': {
                                                              '0%': {
                                                                  filter: 'brightness(1)',
                                                              },
                                                              '50%': {
                                                                  filter: 'brightness(1.5)',
                                                              },
                                                              '100%': {
                                                                  filter: 'brightness(1)',
                                                              },
                                                          },
                                                      }
                                                    : {}
                                            }
                                        />
                                    ) : null}
                                    <Text
                                        color={
                                            isRunning ? 'transparent' : 'whiteAlpha.500'
                                        }
                                        sx={
                                            isRunning
                                                ? {
                                                      background: `linear-gradient(90deg, ${whiteAlpha500}, ${whiteAlpha300}, ${whiteAlpha500})`,
                                                      backgroundSize: '150% 100%',
                                                      backgroundClip: 'text',
                                                      WebkitBackgroundClip: 'text',
                                                      animation:
                                                          'textShine 4s linear infinite',
                                                      '@keyframes textShine': {
                                                          '0%': {
                                                              backgroundPosition:
                                                                  '-300% 0',
                                                          },
                                                          '100%': {
                                                              backgroundPosition:
                                                                  '300% 0',
                                                          },
                                                      },
                                                  }
                                                : {}
                                        }
                                    >
                                        {part.label}
                                    </Text>
                                </HStack>
                            );
                        }
                        // All part types are handled above
                        return null;
                    })}
                </VStack>
            </HStack>
        );
    };

    return (props) => {
        const [whiteAlpha500, whiteAlpha300, whiteAlpha400] = useToken('colors', [
            'whiteAlpha.500',
            'whiteAlpha.300',
            'whiteAlpha.400',
        ]);

        return (
            <Grid
                w="full"
                h="full"
                templateRows="1fr min-content"
                fontSize={{ base: 'sm', '2xl': 'md' }}
            >
                <GridItem pos="relative" w="full">
                    <Stack
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        ref={props.message.container.ref}
                        direction="column"
                        align="start"
                        spacing={2}
                        px={3}
                        pt={4}
                        pb={{ base: 12, xl: 24 }}
                        overflowY="scroll"
                        h="full"
                        onScroll={props.message.container.onScroll}
                        sx={{
                            scrollbarGutter: 'stable both-edges',
                            '&::-webkit-scrollbar': {
                                '-webkit-appearance': 'none',
                                width: '0.5rem',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                            },
                            '&::-webkit-scrollbar-corner': {
                                '-webkit-appearance': 'none',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                borderRadius: '1rem',
                                background: props.message.container.isScrolling
                                    ? 'rgba(255,255,255,0.3)'
                                    : 'transparent',
                                transition: 'background 0.3s ease-in-out',
                            },
                        }}
                    >
                        {props.message.items.map((item) => (
                            <Message key={item.id} {...item} />
                        ))}
                        {/* Render streaming message if it exists */}
                        {props.message.streaming && (
                            <Message {...props.message.streaming} />
                        )}
                        {props.composer.isLoading && (
                            <HStack
                                py={{ base: 1, '2xl': 2 }}
                                color="whiteAlpha.500"
                                fontSize="sm"
                                fontWeight="medium"
                                spacing={2}
                                position="relative"
                            >
                                <Icon
                                    fontSize="md"
                                    as={BiBrain}
                                    color="whiteAlpha.400"
                                    sx={{
                                        animation: 'iconShine 4s linear infinite',
                                        '@keyframes iconShine': {
                                            '0%': {
                                                filter: 'brightness(1)',
                                            },
                                            '50%': {
                                                filter: 'brightness(1.5)',
                                            },
                                            '100%': {
                                                filter: 'brightness(1)',
                                            },
                                        },
                                    }}
                                />
                                <Text
                                    color="whiteAlpha.400"
                                    sx={{
                                        background: `linear-gradient(90deg, ${whiteAlpha500}, ${whiteAlpha300}, ${whiteAlpha500})`,
                                        backgroundSize: '150% 100%',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        animation: 'textShine 4s linear infinite',
                                        '@keyframes textShine': {
                                            '0%': {
                                                backgroundPosition: '-300% 0',
                                            },
                                            '100%': {
                                                backgroundPosition: '300% 0',
                                            },
                                        },
                                    }}
                                >
                                    Thinking
                                </Text>
                            </HStack>
                        )}
                    </Stack>
                </GridItem>
                <GridItem as={VStack} px={3} pb={8} align="start" spacing={2}>
                    <ComposerContainer {...props.composer.container} />
                </GridItem>
            </Grid>
        );
    };
}
